import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {Router} from '@angular/router';
import {Subject, Subscription} from 'rxjs';
import {first, takeUntil} from 'rxjs/operators';

import {fuseAnimations} from '@fuse/animations';
import {CategoryService} from "../../../services/category.service";
import {AccountService, AlertService} from "../../../services";
import {Order} from "../../../models/order";
import {AddProductEmitterService} from "../../service/add-product-emitter.service";
import {Product} from "../../../models/product";
import {AddVoucherEmitterService} from "../../service/add-voucher-emitter.service";
import {VoucherTemplate} from "../../../models/voucherTemplate";
import {OrderItem} from "../../../models/orderItem";
import {OrderVoucherItem} from "../../../models/orderVoucherItem";
import {MatDialog} from "@angular/material/dialog";
import {AdditionalProductInfoDialogComponent} from "../../dialog/additionalProductInfoDialog.component";
import {FormGroup} from "@angular/forms";
import {SelectCustomerDialogComponent} from "../../dialog/selectCustomerDialog/selectCustomerDialog.component";
import {ConfirmBillPrintDialogComponent} from "../../dialog/confirmBillPrintDialog/confirmBillPrintDialog.component";
import {RequiredCustomerDialogComponent} from "../../dialog/requiredCustomerDialog/requiredCustomerDialog.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {OrderService} from "../../../services/order.service";
import {User} from "../../../models";
import {DiscountService} from "../../../discount/service/discount.service";
import {DiscountsDialogComponent} from "../../dialog/discountsDialog/discountsDialog.component";
import {SplitPaymentDialogComponent} from "../../dialog/splitPaymentDialog/splitPaymentDialog.component";
import {VirtualReceiptService} from "../../service/virtualReceipt.servicee";
import {VirtualReceipt} from "../../model/virtualReceipt";
import {VirtualReceiptItem} from "../../model/virtualReceiptItem";

@Component({
    selector: 'receipt-main-sidebar',
    templateUrl: './receipt-sidebar.component.html',
    styleUrls: ['./receipt-sidebar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class ReceiptMainSidebarComponent implements OnInit, OnDestroy {
    folders: any[];
    filters: any[];
    tags: any[];
    accounts: object;
    selectedAccount: string;
    loadingIndicator: boolean;
    // newOrder: Order;
    selectCustomerDialogRef: any;
    confirmBillPrintDialogRef: any;
    requiredCustomerDialogRef: any;
    splitPaymentDialogRef: any;
    isInvoice: boolean = false;
    loading: boolean = false;
    discountsDialogRef: any;
    savingOpenBill: boolean = false;

    // Private
    private _unsubscribeAll: Subject<any>;

    @Input() newOrder;
    @Input() virtualReceipt;
    @Input() customer;

    constructor(
        public _matDialog: MatDialog,
        private _router: Router,
        private addProductEmitterService: AddProductEmitterService,
        private addVoucherEmitterService: AddVoucherEmitterService,
        private _matSnackBar: MatSnackBar,
        private orderService: OrderService,
        private alertService: AlertService,
        private router: Router,
        private discountService: DiscountService,
        private virtualReceiptService: VirtualReceiptService,
    ) {
        // Set the private defaults
        this._unsubscribeAll = new Subject();
        this.loadingIndicator = true;

        if (this.addProductEmitterService.subsAddProduct==undefined) {
            this.addProductEmitterService.subsAddProduct = this.addProductEmitterService.
            invokeAddProduct.subscribe((product:Product) => {
                this.addProduct(product);
            });
        }

        if (this.addVoucherEmitterService.subsAddVoucher==undefined) {
            this.addVoucherEmitterService.subsAddVoucher = this.addVoucherEmitterService.
            invokeAddVoucher.subscribe((voucherTemplate:VoucherTemplate) => {
                this.addVoucher(voucherTemplate);
            });
        }

        this.newOrder = new Order();
        this.newOrder.paymentMethod = 'gotówka';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {

    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this.newOrder = new Order();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    addProduct(product: Product): void {
        let added = false;

        for (let i = 0; i < this.newOrder.items.length; i++) {
            if (this.newOrder.items[i].product !== product) {
                continue;
            }

            added = true;
            this.newOrder.items[i].quantity++;
        }

        if (!added) {
            let orderItem = new OrderItem();
            orderItem.quantity = 1;
            orderItem.priceGross = product.priceGross;
            if (product.vatRate) {
                orderItem.vatRate = product.vatRate.vatValue;
            }
            orderItem.product = product;
            if (product.unit) {
                orderItem.unit = product.unit.id;
            }

            this.newOrder.items.push(orderItem);
        }

        this.recalculateBill();
    }

    addVoucher(voucherTemplate: VoucherTemplate): void {
        let added = false;

        for (let i = 0; i < this.newOrder.vouchers.length; i++) {
            if (this.newOrder.vouchers[i].voucherTemplate !== voucherTemplate) {
                continue;
            }

            added = true;
            this.newOrder.vouchers[i].quantity++;
        }

        if (!added) {
            this.newOrder.addVoucher(voucherTemplate);
        }

        this.recalculateBill();

        if (!this.newOrder.customer) {
            this.showSelectCustomerDialog(null);
        }
    }

    changeProductQuantity(item: OrderItem, quantity: number) {
        item.quantity += quantity;

        if (item.quantity <= 0) {
            for (let i = 0; i < this.newOrder.items.length; i++) {
                if (this.newOrder.items[i].product !== item.product) {
                    continue;
                }

                this.newOrder.items.splice(i, 1);
                break;
            }
        }

        this.recalculateBill();
    }

    setProductQuantity(item: OrderItem, quantity: any) {
        item.quantity = quantity;

        if (item.quantity <= 0) {
            for (let i = 0; i < this.newOrder.items.length; i++) {
                if (this.newOrder.items[i].product !== item.product) {
                    continue;
                }

                this.newOrder.items.splice(i, 1);
                break;
            }
        }

        this.recalculateBill();
    }

    changeVoucherQuantity(item: OrderVoucherItem, quantity: number) {
        item.quantity += quantity;

        if (item.quantity <= 0) {
            for (let i = 0; i < this.newOrder.vouchers.length; i++) {
                if (this.newOrder.vouchers[i].voucherTemplate !== item.voucherTemplate) {
                    continue;
                }

                this.newOrder.vouchers.splice(i, 1);
                break;
            }
        }

        this.recalculateBill();
    }

    setVoucherQuantity(item: OrderVoucherItem, quantity: number) {
        item.quantity = quantity;

        if (item.quantity <= 0) {
            for (let i = 0; i < this.newOrder.vouchers.length; i++) {
                if (this.newOrder.vouchers[i].voucherTemplate !== item.voucherTemplate) {
                    continue;
                }

                this.newOrder.vouchers.splice(i, 1);
                break;
            }
        }

        this.recalculateBill();
    }

    recalculateBill(): void {
        this.newOrder.priceGross = 0;
        this.newOrder.priceGrossBeforeDiscount = 0;

        for (let i = 0; i < this.newOrder.items.length; i++) {
            this.newOrder.priceGross += (this.newOrder.items[i].quantity * this.newOrder.items[i].priceGross);
            this.newOrder.priceGrossBeforeDiscount += (this.newOrder.items[i].quantity * this.newOrder.items[i].priceGrossBeforeDiscount);
        }

        for (let i = 0; i < this.newOrder.vouchers.length; i++) {
            this.newOrder.priceGross += (this.newOrder.vouchers[i].quantity * this.newOrder.vouchers[i].priceGross);
            this.newOrder.priceGrossBeforeDiscount += (this.newOrder.vouchers[i].quantity * this.newOrder.vouchers[i].priceGrossBeforeDiscount);
        }

        this.calculateDiscounts();
    }

    changePaymentMethod(paymentMethod: string): void {
        this.newOrder.paymentMethod = paymentMethod;
    }

    showSelectCustomerDialog(form: any) {
        this.selectCustomerDialogRef = this._matDialog.open(SelectCustomerDialogComponent, {
            panelClass: 'select-customer-dialog',
            data      : {
                action : 'show',
                order: this.newOrder,
            },
            width: '750px',
        });

        this.selectCustomerDialogRef.disableClose = true;
        this.selectCustomerDialogRef.backdropClick().subscribe(_ => {
            return false;
        });

        this.selectCustomerDialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    return;
                }

                this.isInvoice = response.value.invoice;
                this.newOrder.nip = response.value.nip
                this.newOrder.companyCity = response.value.companyCity;
                this.newOrder.companyCountry = response.value.companyCountry;
                this.newOrder.companyName = response.value.companyName;
                this.newOrder.companyPostCode = response.value.companyPostCode;
                this.newOrder.companyStreet = response.value.companyStreet;
                this.newOrder.customer = response.value.customer;
                this.newOrder.worker = response.value.worker;

                this.calculateDiscounts();

                this.selectCustomerDialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }

    showSplitPaymentDialog() {
        this.splitPaymentDialogRef = this._matDialog.open(SplitPaymentDialogComponent, {
            panelClass: 'split-payment-dialog',
            data      : {
                action : 'show',
                order: this.newOrder,
            },
            width: '750px',
        });

        this.splitPaymentDialogRef.disableClose = true;
        this.splitPaymentDialogRef.backdropClick().subscribe(_ => {
            return false;
        });

        this.splitPaymentDialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    return;
                }

                this.newOrder.paymentMethod = response.value.paymentMethod;
                this.newOrder.secondPaymentMethod = response.value.secondPaymentMethod;
                this.newOrder.firstPaymentMethodValue = response.value.firstPaymentMethodValue.replace(',', '.');
                this.newOrder.secondPaymentMethodValue = response.value.secondPaymentMethodValue.replace(',', '.');

                this.splitPaymentDialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }

    showConfirmBillPrintDialog() {
        if (this.newOrder.vouchers.length > 0 && (!this.newOrder.customer || typeof this.newOrder.customer==='undefined')) {
            this.showSelectCustomerDialog(null);
            return;
        }

        this.confirmBillPrintDialogRef = this._matDialog.open(ConfirmBillPrintDialogComponent, {
            panelClass: 'confirm-bill-print-dialog',
            data      : {
                action : 'show',
                order: this.newOrder,
                isInvoice: this.isInvoice,
            },
            width: '750px',
        });

        this.confirmBillPrintDialogRef.disableClose = true;
        this.confirmBillPrintDialogRef.backdropClick().subscribe(_ => {
            return false;
        });

        this.confirmBillPrintDialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    return;
                }

                if (this.newOrder.vouchers.length > 0 && (!this.newOrder.customer || typeof this.newOrder.customer==='undefined')) {
                    this.showRequiredCustomerDialog();
                    return;
                }

                if (!this.isInvoice) {
                    this.newOrder.nip = "";
                    this.newOrder.companyName = "";
                    this.newOrder.companyStreet = "";
                    this.newOrder.companyPostCode = "";
                    this.newOrder.companyCity = "";
                    this.newOrder.companyCountry = "";
                }


                if (this.newOrder.items.length === 0 && this.newOrder.vouchers.length === 0) {
                    this.loading = false;
                    this._matSnackBar.open('Order creation error - Positions are required', 'OK', {
                        verticalPosition: 'top',
                        duration: 5000,
                        panelClass: ['snackbar-error']
                    });

                    return;
                }

                if (this.newOrder.vouchers.length > 0 && (this.newOrder.customer === null || typeof this.newOrder.customer === "undefined")) {
                    this.loading = false;
                    this._matSnackBar.open('Order creation error - Customer is required', 'OK', {
                        verticalPosition: 'top',
                        duration: 5000,
                        panelClass: ['snackbar-error']
                    });

                    return;
                }

                this.orderService.create(this.newOrder)
                    .pipe(first())
                    .subscribe(
                        (data) => {
                            this.alertService.success('Order added successfully');
                            // this.router.navigate(['/orders/details/' + data.id]);

                            this._matSnackBar.open('Order created successfully', 'OK', {
                                verticalPosition: 'top',
                                duration        : 5000,
                                panelClass: ['snackbar-success']
                            });

                            this.newOrder = new Order();
                        },
                        error => {
                            this.loading = false;
                            this._matSnackBar.open('Order creation error - ' + error, 'OK', {
                                verticalPosition: 'top',
                                duration: 5000,
                                panelClass: ['snackbar-error']
                            });
                        });

                this.confirmBillPrintDialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }

    saveBill() {
        this.savingOpenBill = true;
        let virtualReceipt = new VirtualReceipt();
        virtualReceipt.customer = this.newOrder.customer.id;
        let items = [];

        for (let i=0; i<this.newOrder.items.length; i++) {
            let item = new VirtualReceiptItem();
            item.product = this.newOrder.items[i].product.id;
            item.quantity = this.newOrder.items[i].quantity;

            items.push(item);
        }

        for (let i=0; i<this.newOrder.vouchers.length; i++) {
            let item = new VirtualReceiptItem();
            item.voucher = this.newOrder.vouchers[i].voucherTemplate.id;
            item.quantity = this.newOrder.vouchers[i].quantity;

            items.push(item);
        }

        virtualReceipt.items = items;

        if (this.virtualReceipt) {
            this.virtualReceiptService.update(this.virtualReceipt.id, virtualReceipt)
                .pipe(first())
                .subscribe(() => {
                    this._matSnackBar.open("Bill has been saved!", 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-success']
                    });

                    this.savingOpenBill = false;
                }, error => {
                    this._matSnackBar.open(error, 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-error']
                    });

                    this.savingOpenBill = false;
                })
        } else {
            this.virtualReceiptService.create(virtualReceipt)
                .pipe(first())
                .subscribe(() => {
                    this._matSnackBar.open("Bill has been saved!", 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-success']
                    });

                    this.savingOpenBill = false;
                }, error => {
                    this._matSnackBar.open(error, 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-error']
                    });

                    this.savingOpenBill = false;
                })
        }
    }

    showRequiredCustomerDialog() {
        this.requiredCustomerDialogRef = this._matDialog.open(RequiredCustomerDialogComponent, {
            panelClass: 'required-customer-dialog',
            data      : {
                action : 'show',
            },
            width: '350px',
        });

        this.requiredCustomerDialogRef.disableClose = true;
        this.requiredCustomerDialogRef.backdropClick().subscribe(_ => {
            return false;
        });

        this.requiredCustomerDialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                this.calculateDiscounts();
                this.showConfirmBillPrintDialog();
                this.requiredCustomerDialogRef.close();
            }, (err) => {
                console.log(err);
            });
    }

    calculateDiscounts() {
        this.discountService.recalculate(this.newOrder).pipe(first())
            .subscribe(
                (data: Order) => {
                    this.newOrder.priceGross = data.priceGross;
                    this.newOrder.priceGrossBeforeDiscount = data.priceGrossBeforeDiscount;
                    this.newOrder.discountValue = data.discountValue;
                    this.newOrder.items = data.items;
                    this.newOrder.vouchers = data.vouchers;
                    this.newOrder.discounts = data.discounts;
                }
            );
    }

    showDiscountsDialog() {
        this.discountsDialogRef = this._matDialog.open(DiscountsDialogComponent, {
            panelClass: 'discounts-dialog',
            data      : {
                action : 'show',
                discounts: this.newOrder.discounts,
            },
            width: '700px',
        });

        this.discountsDialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    return;
                }

                this.discountsDialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }
}
